/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { Gender } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PatientProps {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  gender: Gender;
  dateOfBirth: Date | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PatientRecord extends Record<PatientProps>({
  id: null as any,
  name: null as any,
  firstName: null as any,
  lastName: null as any,
  gender: null as any,
  dateOfBirth: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    firstName: string | null;
    lastName: string | null;
    gender: Gender;
    dateOfBirth: string | number | Date | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PatientRecord {
    if (json == null) {
      throw new Error('PatientRecord.fromJS: parameter cannot be null');
    }
    return new PatientRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PatientJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'PatientJson.name'),
      firstName: json.firstName,
      lastName: json.lastName,
      gender: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'gender', Gender.Unspecified),
        'PatientJson.gender',
      ),
      dateOfBirth: json.dateOfBirth != null ? new Date(json.dateOfBirth) : null,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PatientJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    firstName: string | null;
    lastName: string | null;
    gender: Gender;
    dateOfBirth: string | number | Date | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PatientRecord, Error[]] {
    if (json == null) {
      throw new Error('PatientRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PatientRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PatientJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'PatientJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: tryOrDefault(
        () => json.firstName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: tryOrDefault(
        () => json.lastName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gender: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'gender', Gender.Unspecified),
            'PatientJson.gender',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOfBirth: tryOrDefault(
        () => (json.dateOfBirth != null ? new Date(json.dateOfBirth) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PatientJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PatientProps) {
    super(props);
    if (props == null) {
      throw new Error('PatientRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PatientViewModelBase implements PatientProps {
  protected readonly _props: PatientProps;
  private _json?: PatientProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get firstName(): string | null {
    return this._props.firstName;
  }
  get lastName(): string | null {
    return this._props.lastName;
  }
  get gender(): Gender {
    return this._props.gender;
  }
  get dateOfBirth(): Date | null {
    return this._props.dateOfBirth;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isFemale() {
    return this._props.gender === Gender.Female;
  }
  public get isMale() {
    return this._props.gender === Gender.Male;
  }
  public get isOther() {
    return this._props.gender === Gender.Other;
  }
  public get isUnspecified() {
    return this._props.gender === Gender.Unspecified;
  }

  public isAnyGender(...gender: Gender[]) {
    return gender.includes(this._props.gender);
  }

  constructor(props: PatientProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        firstName: this._props.firstName,
        lastName: this._props.lastName,
        gender: this._props.gender,
        dateOfBirth: this._props.dateOfBirth,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPatientEntities = (state: AppState) => state.entityStores.patient.entities;

export const selectPatientById = (id: string) => (state: AppState) =>
  state.entityStores.patient.entities.get(id);

export const selectPatientMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.patient.meta.get(id);

export const selectPatientAndMetadataById = (id: string) =>
  createSelector(selectPatientById(id), selectPatientMetadataById(id), (patient, meta) => ({
    patient,
    meta,
  }));

export function selectPatientEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPatientEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PatientRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPatientEntitiesByName(name: string) {
  return createSelector(selectAllPatientEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectPatientEntitiesByFirstName(firstName: string) {
  return createSelector(selectAllPatientEntities, (entities) =>
    entities.filter((e) => e.firstName === firstName).valueSeq(),
  );
}

export function selectPatientEntitiesByLastName(lastName: string) {
  return createSelector(selectAllPatientEntities, (entities) =>
    entities.filter((e) => e.lastName === lastName).valueSeq(),
  );
}

export function selectPatientEntitiesByGender(gender: Gender) {
  return createSelector(selectAllPatientEntities, (entities) =>
    entities.filter((e) => e.gender === gender).valueSeq(),
  );
}

export function selectPatientEntitiesByDateOfBirth(dateOfBirth: Date) {
  return createSelector(selectAllPatientEntities, (entities) =>
    entities.filter((e) => e.dateOfBirth === dateOfBirth).valueSeq(),
  );
}

export function selectPatientEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPatientEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
